// @flow
import React, { useState, Fragment, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, unmaskValue, Button, DateField, HelpDrawerToggle, Alert } from '@cmsgov/ds-healthcare-gov';
import { Dropdown } from '@cmsgov/ds-healthcare-gov-10';
import moment from 'moment';
import { getDateString } from '../../../utilities/date';
import Hint from '../../Hint';
import { runValidations } from '../../../utilities/validation';
import { validators } from '../../../utilities/validators';
import { frequencyAmountToIncome } from '../../../utilities/incomeCalculations';
import { frequencyTypes, frequencyLabel } from '../../../common/enums/FrequencyTypes';
import { incomeTypes, incomeLabel } from '../../../common/enums/IncomeTypes';

import type { FrequencyType } from '../../../common/enums/FrequencyTypes';
import type { IncomeType } from '../../../common/enums/IncomeTypes';
import type { FieldValidation } from '../../../utilities/validation';
import type { Income } from '../IncomeCalculator';

// returned from DateField input
export type DateObject = {
  month: string,
  day: string,
  year: string,
};

type Props = {
  amount?: string,
  calculatedTotal?: number,
  incomeFrequency?: FrequencyType,
  incomeType?: IncomeType,
  onCancelAddIncome: () => void,
  onDoneClick: (incomeRecord: Income) => void,
  showHelpDrawer: ({ heading: string, body: string }) => void,
  activeHelpDrawer: ?string,
  endDate?: DateObject,
  startDate?: DateObject,
};

export const IncomeFields = (props: Props) => {
  const [incomeType, setIncomeType] = useState<string>(props.incomeType || '');
  const [amount, setAmount] = useState<string>(props.amount || '');
  const [incomeFrequency, setIncomeFrequency] = useState<string>(props.incomeFrequency || '');
  const [startDate, setStartDate] = useState<DateObject>(props.startDate || { month: '', day: '', year: '' });
  const [endDate, setEndDate] = useState<DateObject>(props.endDate || { month: '', day: '', year: '' });
  const [formErrors, setErrors] = useState<{ [string]: string }>({});
  const startDateRef = useRef<HTMLDivElement | null>(null);
  const endDateRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const enableDates = !!incomeFrequency && incomeFrequency !== frequencyTypes.ONE_TIME;
    let startDateInputs = [];
    if (startDateRef.current) {
      startDateInputs = startDateRef.current.querySelectorAll('input');
    }
    let endDateInputs = [];
    if (endDateRef.current) {
      endDateInputs = endDateRef.current.querySelectorAll('input');
    }

    [...startDateInputs, ...endDateInputs].forEach((x) => {
      x.disabled = !enableDates;
    });
  }, [incomeFrequency]);

  const shouldShowFPUCChanges = () => {
    const timeToShow = moment('1 Jul 2021 00:00:00 EST');
    return moment().isSameOrAfter(timeToShow);
  };

  const handleUpdateField = async (fieldName: string, value: string, blur: boolean = false) => {
    switch (fieldName) {
      case 'incomeType':
        setIncomeType(value);
        break;
      case 'amount':
        const amount = unmaskValue(value, 'currency');
        setAmount(amount);
        if (blur) {
          await validate('amount');
        }
        break;
      case 'incomeFrequency':
        const blankDate = { month: '', day: '', year: '' };

        // To keep Flow happy, copy errors object to a new one, omitting start and end date errors since we are clearing those values
        const newErrors = {};
        Object.keys(formErrors).forEach((key) => {
          if (key !== 'startDate' && key !== 'endDate') {
            newErrors[key] = formErrors[key];
          }
        });

        setIncomeFrequency(value);
        setStartDate(blankDate);
        setEndDate(blankDate);
        setErrors(newErrors);
        break;
      default:
        break;
    }
    if (fieldName !== 'amount' && formErrors[fieldName]) {
      await validate(fieldName);
    }
  };

  const handleDateUpdateField = async (fieldName: string, value: DateObject) => {
    switch (fieldName) {
      case 'startDate':
        setStartDate(value);
        break;
      case 'endDate':
        setEndDate(value);
        break;
      default:
        throw new Error('Not a valid field name');
    }
    if (formErrors[fieldName]) {
      await validate(fieldName);
    }
  };

  const getValidations = (): Array<FieldValidation<string>> => {
    const validations = [];
    const {
      betweenNegMillionAndPosMillion,
      between0andMillion,
      endDateInvalid,
      frequencyRequired,
      incomeTypeRequired,
      startDateInvalid,
    } = validators;
    const startDateString = getDateString(startDate) || '';
    const endDateString = getDateString(endDate) || '';
    const allowsNegativeIncome = [
      incomeTypes.SELF_EMPLOYMENT,
      incomeTypes.CAPITAL_GAINS,
      incomeTypes.FARMING_OR_FISHING_INCOME,
      incomeTypes.RENTAL_OR_ROYALTY_INCOME,
    ];

    validations.push(['incomeType', incomeTypeRequired, incomeType]);

    if (allowsNegativeIncome.includes(incomeType)) {
      validations.push(['amount', betweenNegMillionAndPosMillion, amount]);
    } else {
      validations.push(['amount', between0andMillion, amount]);
    }
    validations.push(['incomeFrequency', frequencyRequired, incomeFrequency]);
    if (shouldValidateDateFields()) {
      validations.push(['startDate', startDateInvalid, { value: startDateString, endDate: endDateString }]);
      validations.push(['endDate', endDateInvalid, { value: endDateString, startDate: startDateString }]);
    }
    return validations;
  };

  const validate = async (fieldName?: string | Array<string>) => {
    const { errors } = await runValidations(getValidations(), formErrors, fieldName);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDoneClick = async () => {
    //$FlowFixMe
    if (errorRef && errorRef.focus) {
      errorRef.focus();
    }

    if (await validate()) {
      const calculatedTotal = frequencyAmountToIncome(incomeFrequency, amount, startDate, endDate);

      props.onDoneClick({ amount, incomeType, incomeFrequency, startDate, endDate, calculatedTotal });
    }
  };

  const handleCancelClick = () => {
    props.onCancelAddIncome();
  };

  const shouldValidateDateFields = () => {
    return (
      incomeFrequency === frequencyTypes.WEEKLY ||
      incomeFrequency === frequencyTypes.MONTHLY ||
      incomeFrequency === frequencyTypes.YEARLY
    );
  };

  let errorRef = useRef(null);

  return (
    <Fragment>
      <div className="ds-l-md-col--12">
        <div className="ds-l-row">
          <div className="ds-l-md-col--4">
            <Dropdown
              focustrigger="true"
              name="income-type"
              label={t('incomeType')}
              options={[]}
              onChange={(e) => handleUpdateField('incomeType', e.currentTarget.value)}
              onBlur={() => validate('incomeType')}
              value={incomeType}
              errorMessage={formErrors.incomeType}
            >
              <optgroup>
                {incomeType === '' && <option value="">{t(incomeLabel.DEFAULT)}</option>}
                <option value={incomeTypes.JOB}>{t(incomeLabel.JOB)}</option>
                <option value={incomeTypes.SELF_EMPLOYMENT}>{t(incomeLabel.SELF_EMPLOYMENT)}</option>
                <option value={incomeTypes.UNEMPLOYMENT}>{t(incomeLabel.UNEMPLOYMENT)}</option>
                <option value={incomeTypes.SOCIAL_SECURITY_BENEFIT}>{t(incomeLabel.SOCIAL_SECURITY_BENEFIT)}</option>
                <option value={incomeTypes.RETIREMENT}>{t(incomeLabel.RETIREMENT)}</option>
                <option value={incomeTypes.PENSION}>{t(incomeLabel.PENSION)}</option>
                <option value={incomeTypes.INVESTMENT_INCOME}>{t(incomeLabel.INVESTMENT_INCOME)}</option>
                <option value={incomeTypes.CAPITAL_GAINS}>{t(incomeLabel.CAPITAL_GAINS)}</option>
                <option value={incomeTypes.RENTAL_OR_ROYALTY_INCOME}>{t(incomeLabel.RENTAL_OR_ROYALTY_INCOME)}</option>
                <option value={incomeTypes.FARMING_OR_FISHING_INCOME}>
                  {t(incomeLabel.FARMING_OR_FISHING_INCOME)}
                </option>
                <option value={incomeTypes.ALIMONY_RECEIVED}>{t(incomeLabel.ALIMONY_RECEIVED)}</option>
                <option value={incomeTypes.SCHOLARSHIP}>{t(incomeLabel.SCHOLARSHIP)}</option>
              </optgroup>
              <optgroup label={t('moreIncomeSources')}>
                <option value={incomeTypes.CANCELED_DEBT}>{t(incomeLabel.CANCELED_DEBT)}</option>
                <option value={incomeTypes.COURT_AWARDS}>{t(incomeLabel.COURT_AWARDS)}</option>
                <option value={incomeTypes.PRIZES_AWARDS_GAMBLING_WINNINGS}>
                  {t(incomeLabel.PRIZES_AWARDS_GAMBLING_WINNINGS)}
                </option>
                <option value={incomeTypes.JURY_DUTY_PAY}>{t(incomeLabel.JURY_DUTY_PAY)}</option>
                <option value={incomeTypes.OTHER_INCOME}>{t(incomeLabel.OTHER_INCOME)}</option>
              </optgroup>
            </Dropdown>
          </div>
          <div className="ds-l-md-col--8 ds-u-display--flex ds-u-flex-wrap--wrap">
            <TextField
              className="ds-u-margin-right--4"
              ariaLabel="Enter Income amount in dollars."
              label={t('amount')}
              mask="currency"
              inputMode="numeric"
              type="text"
              name="amount"
              size="medium"
              onChange={(e) => handleUpdateField('amount', e.target.value)}
              onBlur={(e) => handleUpdateField('amount', e.target.value, true)} // blur will format the currency to two digits so we should update state
              value={amount}
              errorMessage={formErrors.amount}
            />
            <Dropdown
              name="income-frequency"
              className="ds-l-md-col--4 ds-u-padding--0"
              label={t('howOften')}
              size="medium"
              options={[
                { label: t(frequencyLabel.DEFAULT), value: '' },
                { label: t(frequencyLabel.WEEKLY), value: frequencyTypes.WEEKLY },
                { label: t(frequencyLabel.MONTHLY), value: frequencyTypes.MONTHLY },
                { label: t(frequencyLabel.YEARLY), value: frequencyTypes.YEARLY },
                { label: t(frequencyLabel.ONE_TIME), value: frequencyTypes.ONE_TIME },
              ]}
              onChange={(e) => handleUpdateField('incomeFrequency', e.currentTarget.value)}
              onBlur={() => validate('incomeFrequency')}
              value={incomeFrequency}
              errorMessage={formErrors.incomeFrequency}
            />
          </div>
          <div className="ds-l-md-col--12">
            <HelpDrawerToggle
              helpDrawerOpen={props.activeHelpDrawer === 'helpEstimatingIncome'}
              children={t('helpEstimatingIncomeLink')}
              inline={true}
              showDrawer={() =>
                props.showHelpDrawer({
                  heading: t('helpEstimatingIncomeTitle'),
                  body: shouldShowFPUCChanges() ? t('helpEstimatingIncomeBodyFPUC') : t('helpEstimatingIncomeBody'),
                  id: 'helpEstimatingIncome',
                })
              }
            />
          </div>
        </div>
        <div className="ds-l-row">
          <div className="ds-l-md-col--12">
            <div className="ds-h5 ds-u-margin-top--3">{t('receiveIncome')}</div>
            {<Hint children={t('receiveIncomeHint')} />}
          </div>
          {incomeFrequency === frequencyTypes.ONE_TIME && (
            <Alert
              className="ds-l-md-col--12 ds-u-margin-top--3 ds-u-margin-x--2 ds-c-alert--hide-icon"
              children={t('oneTimeAlert')}
            />
          )}
          <div className="ds-l-md-col--4" ref={startDateRef}>
            <DateField
              label={t('incomeStartDate')}
              name="start-date"
              hint=""
              monthLabel={t('month')}
              dayLabel={t('day')}
              yearLabel={t('year')}
              onChange={(e, dateObj) => handleDateUpdateField('startDate', dateObj)}
              dayName="start-day"
              dayValue={startDate.day}
              monthName="start-month"
              monthValue={startDate.month}
              yearName="start-year"
              yearValue={startDate.year}
              dayInvalid={!!formErrors.startDate}
              monthInvalid={!!formErrors.startDate}
              yearInvalid={!!formErrors.startDate}
              errorMessage={formErrors.startDate}
              onComponentBlur={() => {
                !formErrors.endDate ? validate('startDate') : validate(['startDate', 'endDate']);
              }}
            />
          </div>
          <div className="ds-l-md-col--4" ref={endDateRef}>
            <DateField
              label={t('incomeEndDate')}
              name="end-date"
              hint=""
              monthLabel={t('month')}
              dayLabel={t('day')}
              yearLabel={t('year')}
              onChange={(e, dateObj) => handleDateUpdateField('endDate', dateObj)}
              dayName="end-day"
              dayValue={endDate.day}
              monthName="end-month"
              monthValue={endDate.month}
              yearName="end-year"
              yearValue={endDate.year}
              dayInvalid={!!formErrors.endDate}
              monthInvalid={!!formErrors.endDate}
              yearInvalid={!!formErrors.endDate}
              errorMessage={formErrors.endDate}
              onComponentBlur={() => validate('endDate')}
            />
          </div>
        </div>
        <div className="ds-l-row ds-u-margin-top--3">
          <div className="ds-l-md-col--12">
            <Button
              className="ds-u-margin-right--2 ds-u-margin-bottom--2 ds-c-button--solid"
              onClick={handleDoneClick}
              aria-label={`${t('calculate')} ${t('income')}`}
            >
              {t('calculate')}
            </Button>
            <Button onClick={handleCancelClick} aria-label={`${t('cancel')} ${t('income')}`}>
              {t('cancel')}
            </Button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default IncomeFields;
